/**
 * Wave 4 — Microsite CMS loader.
 *
 * Returns the per-brand MicroSiteContent with any reviewed DB overrides
 * from `microsite_content_blocks` merged on top of the static fallback
 * defined in `microSiteContent.ts`. Zero-risk migration: missing rows
 * fall back to the hardcoded constants.
 *
 * Block keys we recognise (all optional, merged shallow over the base):
 *   hero          → { heroH1, heroSubhead, heroBullets }
 *   services      → { servicesH1, servicesIntro, servicesOutro, pillarBody }
 *   about         → { aboutH1, aboutBody }
 *   faqs          → { faqs: FAQ[] }
 *   serviceDetail:<slug> → Partial<ServiceDetail> (merged into matching service)
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { BrandKey } from "./brandRegistry";
import {
  getMicroSiteContent,
  type MicroSiteContent,
  type ServiceDetail,
} from "./microSiteContent";

interface BlockRow {
  block_key: string;
  path: string;
  content: Record<string, unknown>;
}

const SERVICE_DETAIL_PREFIX = "serviceDetail:";

function applyOverrides(base: MicroSiteContent, rows: BlockRow[]): MicroSiteContent {
  if (!rows.length) return base;
  let next: MicroSiteContent = { ...base, services: [...base.services] };
  for (const row of rows) {
    const c = row.content ?? {};
    if (row.block_key === "hero" || row.block_key === "services" ||
        row.block_key === "about" || row.block_key === "faqs") {
      next = { ...next, ...c } as MicroSiteContent;
    } else if (row.block_key.startsWith(SERVICE_DETAIL_PREFIX)) {
      const slug = row.block_key.slice(SERVICE_DETAIL_PREFIX.length);
      next.services = next.services.map((s) =>
        s.slug === slug ? ({ ...s, ...c } as ServiceDetail) : s,
      );
    }
  }
  return next;
}

async function fetchBlocks(brand: BrandKey): Promise<BlockRow[]> {
  const { data, error } = await supabase
    .from("microsite_content_blocks")
    .select("block_key, path, content")
    .eq("brand", brand)
    .eq("reviewed", true);
  if (error) {
    console.warn("[microsite-cms] fetch failed", error.message);
    return [];
  }
  return (data ?? []) as BlockRow[];
}

interface PaaRow { question: string; answer_draft: string | null }

async function fetchApprovedPaa(brand: BrandKey): Promise<PaaRow[]> {
  const { data, error } = await supabase
    .from("paa_questions")
    .select("question, answer_draft")
    .eq("brand", brand)
    .eq("status", "approved")
    .order("approved_at", { ascending: false })
    .limit(20);
  if (error) {
    console.warn("[paa] fetch failed", error.message);
    return [];
  }
  return (data ?? []) as PaaRow[];
}

export function useMicroSiteContent(brand: BrandKey): MicroSiteContent | null {
  const base = getMicroSiteContent(brand);
  const { data: rows } = useQuery({
    queryKey: ["microsite-content-blocks", brand],
    queryFn: () => fetchBlocks(brand),
    staleTime: 5 * 60 * 1000,
    enabled: !!base,
  });
  const { data: paa } = useQuery({
    queryKey: ["paa-approved", brand],
    queryFn: () => fetchApprovedPaa(brand),
    staleTime: 5 * 60 * 1000,
    enabled: !!base,
  });
  if (!base) return null;
  const merged = applyOverrides(base, rows ?? []);
  // Append approved PAA answers as additional FAQs (dedupe by question text).
  const existingQs = new Set(merged.faqs.map((f) => f.q.toLowerCase().trim()));
  const extra = (paa ?? [])
    .filter((p) => p.answer_draft && !existingQs.has(p.question.toLowerCase().trim()))
    .map((p) => ({ q: p.question, a: p.answer_draft as string }));
  return extra.length ? { ...merged, faqs: [...merged.faqs, ...extra] } : merged;
}

